<template>
     <MessageCard
            message="Select a coverage option, from the offerings below, by clicking the applicable box. Failure to make a selection will result in the Fund determining your enrollment for the upcoming quarter." type="primary"
            iconClass="iatse-icon-alarm"/>
    <div class="plan-content">
        <div class="container-fluid" v-if="dataLoaded">
            <div class="row">
                <div class="col-md-12 col-sm-12 d-flex gap-15 flex-column">
                    <div class="w-100 d-flex justify-content-between gap-15 flex-md-row flex-column">
                        <h3 class="d-flex align-items-end gap-5">
                            <span>Coverage Options</span>
                            <span class="tooltip-wrapper d-flex align-items-end">
                                <i class="iatse-icon-info-tooltip icon_rotate_0"></i>
                                <p class="tooltip-text tooltip-text_secondary tooltip-text__large tooltip-text__bottom">
                                    please click the Benefits at a Glance button to the right to view all Plan C options.
                                </p>
                            </span>
                        </h3>
                        <a :href="`${baseUrl}/storage/app/media/documents/Plan%20C%20Benefits%20at%20a%20Glance%20-%201-1-2025.pdf`" target="_blank" class="links">Benefit at a Glance</a>
                    </div>
                    <h5>
                        Your coverage selection and any applicable payment is due no later than <b>{{ dueDate }}</b>
                        for the coverage period starting <b>{{ coveragePeriod }}</b>
                    </h5>
                </div>
            </div>

            <!-- Display Plan C Options -->
            <!-- <div class="d-flex align-items-stretch flex-md-row flex-column flex-wrap row-cols-1 row-cols-md-4 mt-20 gap-20"> -->
            <div class="d-flex align-items-stretch flex-md-row flex-column flex-wrap mt-20 gap-10 flex-payment-option-cards">
                <!-- v-for over groupedOptions, skipping MRP (shown separately) -->
                <div
                    class="capp-statement-item"
                        v-for="(planData, plan, index) in groupedOptions"
                        :key="plan"
                    >
                    <div class="capp-statement" :class="`capp-statement__color-${(index % 5) + 1}`">

                        <!-- TOGGLE BUTTONS: only show if there is both Single AND Family -->
                        <div class="capp-statement__toggle d-flex justify-content-center mb-30"
                             v-if="hasFamilyAndSingle(coverageOptions[plan])">
                            <div class="toggle-switch">
                                <button
                                    :class="{ active: !familyToggle[plan] }"
                                    @click="familyToggle[plan] = false"
                                >
                                    Single
                                </button>
                                <button
                                    :class="{ active: familyToggle[plan] }"
                                    @click="familyToggle[plan] = true"
                                >
                                    Family
                                </button>
                            </div>
                        </div>

                        <!-- PLAN DETAILS -->
                        <div class="capp-statement__details">
                            <h3 v-if="planData?.current">{{ planData.current.Title }}</h3>

                            <div class="capp-statement__owed rounded mt-15">
                                <p>Amount Owed</p>
                                <h4>{{ formatCurrency(planData.current.AmountOwed) }}</h4>
                            </div>
                        </div>

                        <!-- ACTION BUTTON -->
                        <div class="capp-statement__action mt-30">
                            <button
                                class="btn btn-primary w-100"
                                :class="{ 'btn-selected': isInvoiceSelected(planData.current.OptionKey) }"
                                @click="selectOption(plan, planData.current)"
                            >
                                {{ planData.current.IsCurrent
                                    ? 'Continue this coverage'
                                    : 'Select this coverage'
                                }}
                            </button>
                        </div>
                    </div>
                </div>
                <!-- MRP option (if available) -->
                <div class="col capp-statement-item w-sm-100" v-if="coverageOptions['MRP']">
                    <div class="w-100 capp-statement capp-statement__mrp">
                        <div class="capp-statement__details">
                            <h3>{{ coverageOptions['MRP'].Title }}</h3>
                            <div class="capp-statement__owed rounded mt-15">
                                <p>Amount Owed</p>
                                <h4>{{ formatCurrency(coverageOptions['MRP'].AmountOwed) }}</h4>
                            </div>
                        </div>
                        <div class="capp-statement__action mt-30">
                            <button
                                class="btn btn-primary w-100"
                                :class="{ 'btn-selected': isInvoiceSelected(coverageOptions['MRP'].OptionKey) }"
                                @click="selectOption('MRP', coverageOptions['MRP'])"
                            >
                                <span v-if="hasMrpDocuments">
                                    {{ coverageOptions['MRP'].IsCurrent ? 'Continue this coverage' : 'Select this coverage' }}
                                </span>
                                <span v-else>Related Instructions</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div> <!-- end row -->
        </div>
    </div>
</template>

<script lang="ts">
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { useModalStore } from '@/stores/modal';
import { useCurrencyFormatter } from "@/composable/useCurrencyFormatter";
import { CappStatementData, PlanCOptions, CappStatementOption, CoverageOptions } from '@/interfaces/health';
import { useCappInvoicesStore } from '@/stores/cappInvoice';
import { useCoverageStore } from '@/stores/coverage';
import { useParticipantStore } from '@/stores/participant';

export default {
    setup() {
        const dataLoaded = ref(false);
        const coverageOptions = ref<CoverageOptions>({});
        const dueDate = ref('');
        const coveragePeriod = ref('');
        const hasMrpDocuments = ref(false);

        // familyToggle[plan] = true means "Family" is selected for that plan, false => "Single"
        const familyToggle = ref<{ [key: string]: boolean }>({});

        const openModal = useModalStore().openModal;
        const { formatCurrency } = useCurrencyFormatter();
        const cappInvoicesStore = useCappInvoicesStore();
        const coverageStore = useCoverageStore();

        // --- Fetch Data ------------------------------------------------------
        const fetchData = () => {
            axios
                .get('/api/participant/health-welfare/capp-statement')
                .then((response) => {
                    coverageOptions.value = response.data.PlanCStatement.CoverageOptions;
                    hasMrpDocuments.value = response.data.HasMrpDocuments;
                    dueDate.value = response.data.PlanCStatement.DueDate;
                    coveragePeriod.value = response.data.PlanCStatement.CoveragePeriod;
                    dataLoaded.value = true;

                    initializeFamilyToggle();
                })
                .catch((error) => {
                    console.error('Error fetching data:', error);
                });
        };

        // --- Initialize toggles based on the presence of a current Family plan
        const initializeFamilyToggle = () => {
            Object.keys(coverageOptions.value).forEach((plan) => {
                if (plan === 'MRP') return;

                const planObj = coverageOptions.value[plan];
                if (!isPlanCOptions(planObj)) return;

                const hasSingle = !!planObj.Single;
                const hasFamily = !!planObj.Family;

                if (hasSingle && hasFamily) {
                    // Prefer whichever is marked current; default to Single
                    familyToggle.value[plan] = !!planObj.Family?.IsCurrent;
                } else if (hasFamily && !hasSingle) {
                    // Only Family exists
                    familyToggle.value[plan] = true;
                } else {
                    // Only Single exists or neither (defensive)
                    familyToggle.value[plan] = false;
                }
            });
        };

        // --- Utility: check if this plan has both Single & Family
        function hasFamilyAndSingle(option: any): option is PlanCOptions {
            return (
                option && 
                option.Single !== undefined && 
                option.Family !== undefined
            );
        }

        function isPlanCOptions(obj: unknown): obj is PlanCOptions {
            // A minimal check: if it has 'Single' or 'Family', it’s PlanCOptions
            return (
                typeof obj === 'object' &&
                obj !== null &&
                ('Single' in obj || 'Family' in obj)
            );
            }

        // --- "groupedOptions": pick correct coverage object based on toggle
        const groupedOptions = computed(() => {
            const grouped: {
                [plan: string]: {
                    current: CappStatementOption
                }
            } = {};

            Object.keys(coverageOptions.value)
                .filter((plan) => plan !== 'MRP')
                .forEach((plan) => {
                    const planOptions = coverageOptions.value[plan];
                    if (!planOptions) return;

                    if (isPlanCOptions(planOptions)) {
                        const hasSingle = !!planOptions.Single;
                        const hasFamily = !!planOptions.Family;

                        // Decide which to show:
                        // - If both exist, use toggle
                        // - If only one exists, use that one regardless of toggle
                        let useFamily = false;
                        if (hasSingle && hasFamily) {
                            useFamily = !!familyToggle.value[plan];
                        } else if (hasFamily && !hasSingle) {
                            useFamily = true;
                        } else {
                            useFamily = false;
                        }

                        const current =
                            (useFamily ? planOptions.Family : planOptions.Single) ??
                            // final fallback just in case
                            planOptions.Family ??
                            planOptions.Single;

                        if (current) {
                            grouped[plan] = {
                                current
                            };
                        }
                    } else {
                        // Non PlanCOptions (single object case)
                        grouped[plan] = {
                            current: planOptions as CappStatementOption
                        };
                    }
                });

            return grouped;
        });


        // --- Selecting an option triggers either a zero-balance process or checkout modal
        const selectOption = (plan: string, option: CappStatementOption) => {
            const userConfirmed = confirm(`You have selected ${option.Title}. Are you sure?`);
            if (userConfirmed) {
                cappInvoicesStore.setSelectedInvoice(option);

                if (plan === 'MRP') {
                    if (!hasMrpDocuments.value) {
                        openModal('hw-mrp-terms', 'Certification', 'plan-c-mrp active');
                    } else {
                        processZeroBalance();
                    }
                } else {
                    if (parseFloat(String(option.AmountOwed)) === 0) {
                        processZeroBalance();
                    } else {
                        openModal('hw-capp-checkout', 'Make CAPP Co-Payment', 'plan-c-mrp active');
                    }
                }
            }
        };

        // --- If plan's AmountOwed is zero, call the endpoint directly
        const processZeroBalance = async () => {
            try {
                const data = {
                    option: cappInvoicesStore.selectedInvoice?.OptionKey ?? null,
                };
                const response = await axios.post('/api/checkout/capp', data);

                if (response.status >= 200 && response.status < 300) {
                    const { success, transaction_id, error_code, error_message } = response.data.data;
                    if (success) {
                        openModal('payment-status', '', 'modal-success', null, null, undefined, undefined, {
                            success: true,
                            transactionId: transaction_id,
                            message: 'Coverage Selection has been made.',
                            type: 'capp'
                        });

                        // Refresh coverage data
                        await coverageStore.fetchHealthData();
                    } else {
                        openModal('payment-status', '', 'modal-success', null, null, undefined, undefined, {
                            success: false,
                            errorCode: error_code,
                            message: error_message || 'Payment was unsuccessful.',
                            type: 'capp'
                        });
                    }
                } else {
                    openModal('payment-status', '', 'modal-success', null, null, undefined, undefined, {
                        success: false,
                        message: 'An unexpected error occurred. Please try again later.',
                        type: 'capp'
                    });
                }
            } catch (err) {
                let errorMessage = 'An error occurred during payment processing.';
                if (axios.isAxiosError(err)) {
                    if (err.response) {
                        const { status, data } = err.response;
                        console.error(`Error response status: ${status}`, data);
                        errorMessage = data?.error_message || data?.message || errorMessage;
                    } else if (err.request) {
                        console.error('No response received:', err.request);
                        errorMessage = 'No response from the server. Please check your network connection.';
                    } else {
                        console.error('Error', err.message);
                        errorMessage = err.message;
                    }
                } else {
                    console.error('Unexpected error:', err);
                }

                openModal('payment-status', '', 'modal-success', null, null, undefined, undefined, {
                    success: false,
                    message: errorMessage,
                    type: 'capp'
                });
            }

            useParticipantStore().fetchAlerts();
        };

        onMounted(() => {
            fetchData();
        });

        return {
            dataLoaded,
            coverageOptions,
            dueDate,
            coveragePeriod,
            hasMrpDocuments,
            baseUrl: process.env.VUE_APP_PUBLIC_URL,

            // toggles
            familyToggle,

            // computed & utilities
            groupedOptions,
            hasFamilyAndSingle,

            // composable
            formatCurrency,

            // actions
            selectOption,
            isInvoiceSelected: cappInvoicesStore.isInvoiceSelected
        };
    },
};
</script>

<style scoped lang="scss">
    /* target your 4 boxes inside the flex parent */
    .flex-payment-option-cards > * {
        flex: 1 1 100%;
    }

    /* 2 columns from sm and up (optional) */
    @media (min-width: 576px) {
        .flex-payment-option-cards > * { flex: 1 1 calc(50% - 10px); }
    }

    /* force 2 columns specifically at ≥1350px */
    @media (min-width: 1350px) {
        .flex-payment-option-cards > * { flex: 1 1 calc(50% - 10px); }
    }

    /* jump to 4 columns at ≥1400px */
    @media (min-width: 1400px) {
        .flex-payment-option-cards > * { flex: 1 1 calc(20% - 10px); }
    }
</style>